import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, ChevronDown, ChevronUp } from "lucide-react";

interface TicketReplyProps {
  ticketId: string;
  defaultTo?: string;
  onSent: () => void;
}

export function TicketReply({ ticketId, defaultTo, onSent }: TicketReplyProps) {
  const { toast } = useToast();
  const [htmlBody, setHtmlBody] = useState("");
  const [to, setTo] = useState(defaultTo ?? "");
  const [cc, setCc] = useState("");
  const [showFields, setShowFields] = useState(false);

  // Sync default "to" when it changes (e.g. new ticket selected)
  useEffect(() => {
    if (defaultTo) setTo(defaultTo);
  }, [defaultTo]);

  const replyMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/support/tickets/${ticketId}/reply`, {
        htmlBody,
        to,
        cc: cc || undefined,
      });
    },
    onSuccess: () => {
      setHtmlBody("");
      setCc("");
      setShowFields(false);
      toast({ title: "Reply sent" });
      onSent();
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to send reply",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-2">
      {/* Compact To display + expand toggle */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground shrink-0">To:</span>
        {showFields ? (
          <Input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com"
            className="h-7 text-xs"
          />
        ) : (
          <span className="text-foreground truncate">{to || "—"}</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={() => setShowFields(!showFields)}
        >
          {showFields ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </Button>
      </div>

      {/* Expanded CC field */}
      {showFields && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground shrink-0">Cc:</span>
          <Input
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="cc@example.com"
            className="h-7 text-xs"
          />
        </div>
      )}

      {/* Message + Send */}
      <Textarea
        placeholder="Type your reply..."
        className="min-h-[80px] text-sm"
        value={htmlBody}
        onChange={(e) => setHtmlBody(e.target.value)}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => replyMutation.mutate()}
          disabled={replyMutation.isPending || !htmlBody.trim() || !to.trim()}
        >
          {replyMutation.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="mr-1.5 h-3.5 w-3.5" />
          )}
          Send
        </Button>
      </div>
    </div>
  );
}
